import { Box } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import {
  useViews,
  useCreateView,
  useUpdateView,
} from "../../../hooks/useView";
import { useGetRoles } from "../../../hooks/useRole";
import type View from "../../../types/view/View";
import type Role from "../../../types/role/Role";
import {
  toolbarBtnSx,
  flyoutInputSx,
  flyoutLabelSx,
  crudTableSx,
  selectedRowSx,
  radioInputSx,
  primaryCellSx,
  cancelBtnSx,
  CrudPageLayout,
  LoadingState,
  EmptyState,
  FlyoutDrawer,
  FlyoutFooter,
  PlusIcon,
  TrashIcon,
  flyoutLabels,
  formErrorSx,
} from "../../components/shared";

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function V2Views() {
  const { t } = useTranslation(["common", "errors"]);
  const { customerId } = useParams<{ customerId: string }>();
  const { user: authUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // -- Feature flag gate ------------------------------------------------------
  const hasViewsFlag = authUser?.hasFeatureFlag("Views") ?? false;

  // -- Data -----------------------------------------------------------------
  const { data: views, isLoading: viewsLoading } = useViews(customerId ?? "");
  const { data: roles } = useGetRoles(customerId);

  const rolesMap = useMemo(() => {
    const m = new Map<string, Role>();
    if (roles) {
      for (const r of roles as Role[]) {
        m.set(r.roleId, r);
      }
    }
    return m;
  }, [roles]);

  // -- Mutations ------------------------------------------------------------
  const createViewMutation = useCreateView();
  const updateViewMutation = useUpdateView();

  // -- Selection ------------------------------------------------------------
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const selectedView =
    (views as View[] | undefined)?.find((v) => v.viewId === selectedViewId) ?? null;

  // -- Flyout state ---------------------------------------------------------
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutMode, setFlyoutMode] = useState<"create" | "edit">("create");

  // -- Flyout form state ----------------------------------------------------
  const [formName, setFormName] = useState("");
  const [formRoleId, setFormRoleId] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formRetentionDate, setFormRetentionDate] = useState("");
  const [formError, setFormError] = useState("");

  // -- Delete contact support dialog state ----------------------------------
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);

  // -- Permissions ----------------------------------------------------------
  const canCreate = authUser?.hasPermission("views:Create") ?? false;
  const canEdit = authUser?.hasPermission("views:Update") ?? false;
  const canDelete = authUser?.hasPermission("views:Delete") ?? false;

  // -- Mutation success handler ---------------------------------------------
  const handleMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["views"] });
  };

  // -- Reset form -----------------------------------------------------------
  const resetForm = () => {
    setFormName("");
    setFormRoleId("");
    setFormDescription("");
    setFormRetentionDate("");
    setFormError("");
    setShowDeleteMessage(false);
  };

  // -- Populate form from view ----------------------------------------------
  const populateFromView = (view: View) => {
    setFormName(view.name);
    setFormRoleId(view.roleId);
    setFormDescription(view.description);
    if (view.retentionDate) {
      // Convert Unix timestamp (seconds) to datetime-local string
      const date = new Date(view.retentionDate * 1000);
      const localISO = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setFormRetentionDate(localISO);
    } else {
      setFormRetentionDate("");
    }
    setFormError("");
    setShowDeleteMessage(false);
  };

  // -- Handlers -------------------------------------------------------------
  const handleRowSelect = (viewId: string) => {
    if (!canEdit) {
      setSelectedViewId((prev) => (prev === viewId ? null : viewId));
      return;
    }
    const view = (views as View[] | undefined)?.find((v) => v.viewId === viewId);
    if (!view) return;

    if (selectedViewId === viewId && flyoutOpen) {
      setFlyoutOpen(false);
      setSelectedViewId(null);
      return;
    }

    setSelectedViewId(viewId);
    setFlyoutMode("edit");
    setFlyoutOpen(true);
    populateFromView(view);
  };

  useEffect(() => {
    if (flyoutOpen && flyoutMode === "edit" && selectedView) {
      populateFromView(selectedView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView?.viewId]);

  const handleCreate = () => {
    setSelectedViewId(null);
    setFlyoutMode("create");
    setFlyoutOpen(true);
    resetForm();
  };

  const handleCloseFlyout = () => {
    setFlyoutOpen(false);
    resetForm();
  };

  const handleSave = () => {
    if (!formName.trim()) {
      setFormError(t("common:viewNameRequired"));
      return;
    }
    if (!formRoleId) {
      setFormError(t("common:roleRequired"));
      return;
    }
    if (!formDescription.trim()) {
      setFormError(t("common:descriptionRequired"));
      return;
    }
    setFormError("");

    // Convert datetime-local value to Unix timestamp (seconds)
    const retentionDate = formRetentionDate
      ? Math.floor(new Date(formRetentionDate).getTime() / 1000)
      : undefined;

    if (flyoutMode === "create") {
      createViewMutation.mutate(
        {
          customerId: customerId ?? "",
          roleId: formRoleId,
          name: formName.trim(),
          description: formDescription.trim(),
          retentionDate,
        },
        {
          onSuccess: () => {
            handleMutationSuccess();
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("common:failedToCreateView")),
        },
      );
    } else if (selectedView) {
      const role = rolesMap.get(formRoleId) ?? null;
      updateViewMutation.mutate(
        {
          customerId: customerId ?? "",
          viewId: selectedView.viewId,
          role,
          name: formName.trim(),
          description: formDescription.trim(),
          retentionDate,
        },
        {
          onSuccess: () => {
            handleMutationSuccess();
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("common:failedToUpdateView")),
        },
      );
    }
  };

  const handleOpenView = (e: React.MouseEvent, viewId: string) => {
    e.stopPropagation();
    navigate(`/customers/${customerId}/v2/views/${viewId}`);
  };

  // -- Format date helper ---------------------------------------------------
  const formatExpiration = (retentionDate?: number | null): string => {
    if (!retentionDate) return "\u2014";
    return new Date(retentionDate * 1000).toLocaleDateString();
  };

  const isSaving = createViewMutation.isPending || updateViewMutation.isPending;

  // -- Feature flag gate render ---------------------------------------------
  if (!hasViewsFlag) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "var(--text-tertiary)",
          fontSize: "0.875rem",
        }}
      >
        {t("common:viewsFeatureNotEnabled")}
      </Box>
    );
  }

  // -- Render ---------------------------------------------------------------

  const toolbarContent = (
    <>
      {canCreate && (
        <Box component="button" onClick={handleCreate} sx={toolbarBtnSx(false)}>
          <PlusIcon />
          {t("common:createView")}
        </Box>
      )}
      {canDelete && (
        <Box
          component="button"
          onClick={() => {
            if (selectedView) setShowDeleteMessage(true);
          }}
          sx={toolbarBtnSx(!selectedView, true)}
        >
          <TrashIcon />
          {t("common:deleteView")}
        </Box>
      )}
    </>
  );

  const tableContent = viewsLoading ? (
    <LoadingState message={t("common:loadingViews")} />
  ) : !views || (views as View[]).length === 0 ? (
    <EmptyState message={t("common:noViewsFound")} />
  ) : (
    <Box component="table" sx={crudTableSx}>
      <thead>
        <tr>
          <Box component="th" sx={{ width: 40, textAlign: "center" }}>
            {/* Radio header -- intentionally blank */}
          </Box>
          <th>{t("common:name")}</th>
          <th>{t("common:roles")}</th>
          <th>{t("common:description")}</th>
          <th>{t("common:expirationDate")}</th>
          <Box component="th" sx={{ width: 80 }}>
            {/* Open action header */}
          </Box>
        </tr>
      </thead>
      <tbody>
        {(views as View[]).map((view) => {
          const isSelected = selectedViewId === view.viewId;
          const roleName = rolesMap.get(view.roleId)?.name ?? "\u2014";
          return (
            <Box
              component="tr"
              key={view.viewId}
              onClick={() => handleRowSelect(view.viewId)}
              sx={selectedRowSx(isSelected)}
            >
              {/* Radio */}
              <Box component="td" sx={{ textAlign: "center" }}>
                <Box
                  component="input"
                  type="radio"
                  name="view-select"
                  checked={isSelected}
                  onChange={() => handleRowSelect(view.viewId)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={radioInputSx}
                />
              </Box>

              {/* Name */}
              <Box component="td" sx={primaryCellSx}>
                {view.name}
              </Box>

              {/* Role */}
              <td>{roleName}</td>

              {/* Description */}
              <Box
                component="td"
                sx={{
                  maxWidth: 300,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {view.description}
              </Box>

              {/* Expiration */}
              <td>{formatExpiration(view.retentionDate)}</td>

              {/* Open button */}
              <td>
                <Box
                  component="button"
                  onClick={(e: React.MouseEvent) => handleOpenView(e, view.viewId)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    px: "10px",
                    py: "4px",
                    borderRadius: "6px",
                    border: "1px solid var(--border-mid)",
                    bgcolor: "transparent",
                    color: "var(--accent)",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: "var(--bg-hover)",
                    },
                  }}
                >
                  {t("common:open")}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 12, height: 12 }}>
                    <path d="M5 12h14" />
                    <path d="M12 5l7 7-7 7" />
                  </svg>
                </Box>
              </td>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );

  const { flyoutTitle, saveLabel } = flyoutLabels(flyoutMode, isSaving, {
    createTitle: t("common:createView"), editTitle: t("common:editView"),
    creating: t("common:creating"), saving: t("common:saving"), saveChanges: t("common:saveChanges"),
  });

  return (
    <CrudPageLayout
      toolbar={toolbarContent}
      extra={
        <>
          {/* Delete contact support dialog */}
          {showDeleteMessage && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.5)",
              }}
              onClick={() => setShowDeleteMessage(false)}
            >
              <Box
                sx={{
                  backgroundColor: "var(--bg-surface)",
                  borderRadius: "12px",
                  border: "1px solid var(--border-mid)",
                  padding: "24px",
                  maxWidth: 440,
                  width: "90%",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Box
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                    mb: "12px",
                  }}
                >
                  {t("common:deleteView")}
                </Box>
                <Box sx={{ color: "var(--text-secondary)", fontSize: "0.875rem", lineHeight: 1.6, mb: "20px" }}>
                  {t("common:deleteViewContactSupport")}{" "}
                  <Box
                    component="a"
                    href="mailto:support@wilmactech.com"
                    sx={{ color: "var(--accent)", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    support@wilmactech.com
                  </Box>
                  .
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Box
                    component="button"
                    onClick={() => setShowDeleteMessage(false)}
                    sx={cancelBtnSx}
                  >
                    {t("common:close")}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <FlyoutDrawer
            open={flyoutOpen}
            onClose={handleCloseFlyout}
            title={flyoutTitle}
            footer={
              <FlyoutFooter
                onCancel={handleCloseFlyout}
                cancelLabel={t("common:cancel")}
                onSave={handleSave}
                saveLabel={saveLabel}
                isSaving={isSaving}
              />
            }
          >
            {formError && (
              <Box sx={formErrorSx}>{formError}</Box>
            )}

            {/* Name */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("common:name")}</Box>
              <Box
                component="input"
                type="text"
                placeholder={t("common:viewNamePlaceholder")}
                value={formName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormName(e.target.value)}
                sx={flyoutInputSx}
              />
            </Box>

            {/* Role */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("common:roles")}</Box>
              <Box
                component="select"
                value={formRoleId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormRoleId(e.target.value)}
                sx={{
                  ...flyoutInputSx,
                  appearance: "auto" as const,
                }}
              >
                <option value="">{t("common:selectRole")}</option>
                {((roles as Role[] | undefined) ?? []).map((r) => (
                  <option key={r.roleId} value={r.roleId}>
                    {r.name}
                  </option>
                ))}
              </Box>
            </Box>

            {/* Description */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("common:description")}</Box>
              <Box
                component="textarea"
                placeholder={t("common:viewDescriptionPlaceholder")}
                value={formDescription}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormDescription(e.target.value)}
                rows={3}
                sx={{
                  ...flyoutInputSx,
                  resize: "vertical" as const,
                  minHeight: "80px",
                }}
              />
            </Box>

            {/* Expiration Date */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("common:expirationDateOptional")}</Box>
              <Box
                component="input"
                type="datetime-local"
                value={formRetentionDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormRetentionDate(e.target.value)}
                sx={{
                  ...flyoutInputSx,
                  colorScheme: "dark",
                }}
              />
            </Box>
          </FlyoutDrawer>
        </>
      }
    >
      {tableContent}
    </CrudPageLayout>
  );
}
